import { type AnyRoute } from '@tanstack/react-router';
import { AnimatePresence, motion } from 'motion/react';
import {
  type ComponentPropsWithRef,
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import appIconUrl from '@/assets/app-icon.png';
import { Icons } from '@/assets/icons/icons';
import { XStack, YStack } from '@/components/ui';
import { formatShortcutKeys } from '@/features/shortcuts';
import { trackEvent } from '@/utils/analytics';
import { classList } from '@/utils/cn';

import { electronApis } from '../electron-api';

import { lbNotificationsApis } from './lb-notifications.apis';

interface ViewProps {
  children: ReactNode;
}

export const Root = ({ children }: ViewProps) => {
  const state = lbNotificationsApis.useState();
  return (
    <div className="pt-7 w-screen h-screen flex items-start justify-end">
      <AnimatePresence mode="wait">
        {state === 'enter' && children}
      </AnimatePresence>
    </div>
  );
};

const SWIPE_DISMISS_THRESHOLD = 80;

const HoverContext = createContext(false);

export const AUTO_ACTION_DURATION_MS = 10000;

interface UseNotificationTimerOptions {
  durationMs?: number | null;
  onComplete: () => void;
}

/**
 * Auto-dismiss timer that pauses on hover.
 * Returns `progress` (100 to 0) for optional visual rendering.
 */
export function useNotificationTimer({
  durationMs,
  onComplete,
}: UseNotificationTimerOptions) {
  const isDisabled = durationMs === null;
  const effectiveDuration = durationMs ?? AUTO_ACTION_DURATION_MS;
  const state = lbNotificationsApis.useState();
  const hovered = useContext(HoverContext);
  const [progress, setProgress] = useState(100);
  const remainingRef = useRef(effectiveDuration);
  const startTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isActiveRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const durationMsRef = useRef(effectiveDuration);
  durationMsRef.current = effectiveDuration;

  const clearScheduledWork = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const tick = useCallback(() => {
    if (!isActiveRef.current || startTimeRef.current === null) return;
    const elapsed = performance.now() - startTimeRef.current;
    const remaining = Math.max(0, remainingRef.current - elapsed);
    setProgress((remaining / durationMsRef.current) * 100);

    if (remaining <= 0) {
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => {
    if (isDisabled || state !== 'enter') {
      isActiveRef.current = false;
      startTimeRef.current = null;
      clearScheduledWork();
      return;
    }

    isActiveRef.current = true;

    if (hovered) {
      clearScheduledWork();
      if (startTimeRef.current !== null) {
        const elapsed = performance.now() - startTimeRef.current;
        remainingRef.current = Math.max(0, remainingRef.current - elapsed);
        startTimeRef.current = null;
      }
    } else {
      clearScheduledWork();
      startTimeRef.current = performance.now();
      timeoutRef.current = setTimeout(() => {
        if (!isActiveRef.current) return;
        startTimeRef.current = null;
        remainingRef.current = 0;
        setProgress(0);
        onCompleteRef.current();
      }, remainingRef.current);
      rafRef.current = requestAnimationFrame(tick);
    }

    return clearScheduledWork;
  }, [clearScheduledWork, hovered, isDisabled, state, tick]);

  return { progress };
}

interface NotificationProgressBarProps {
  onComplete: () => void;
  durationMs?: number;
}

export function NotificationProgressIndicator({
  progress,
  className,
}: {
  progress: number;
  className?: string;
}) {
  return (
    <div className="absolute -z-10 bottom-0 left-0 right-0 h-1">
      <div
        className={classList(
          'h-full rounded-lg bg-green-400/60 dark:bg-green-400/40',
          className
        )}
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

export function NotificationProgressBar({
  onComplete,
  durationMs,
}: NotificationProgressBarProps) {
  const { progress } = useNotificationTimer({ durationMs, onComplete });

  return <NotificationProgressIndicator progress={progress} />;
}

export const Frame = ({ children }: ViewProps) => {
  const state = lbNotificationsApis.useState();
  const notification = lbNotificationsApis.useNotification();
  const dismiss = lbNotificationsApis.useDismiss();
  const queueCount = lbNotificationsApis.useQueueCount();
  const [showDismiss, setShowDismiss] = useState(false);

  const notificationId = notification?.id;

  const reportInteraction = (interacting: boolean) => {
    if (!notificationId) return;

    electronApis.lbNotifications.interactionChanged({
      notificationId,
      interacting,
    });
  };

  const handleMouseEnter = () => {
    if (state !== 'enter') return;

    electronApis.window.setIgnoreMouseEvents(false);
    setShowDismiss(true);
    reportInteraction(true);
  };

  const handleMouseLeave = () => {
    electronApis.window.setIgnoreMouseEvents(true, { forward: true });
    setShowDismiss(false);
    reportInteraction(false);
  };

  useEffect(() => {
    if (!notificationId) return;

    return () => {
      electronApis.lbNotifications.interactionChanged({
        notificationId,
        interacting: false,
      });
    };
  }, [notificationId]);

  const handleAnimationComplete = ({ opacity }: { opacity: number }) => {
    if (opacity >= 0.9 && notification) {
      electronApis.lbNotifications.presented(notification);
    }
  };

  return (
    <motion.div
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={{ left: 0, right: 0.5 }}
      onDragEnd={(_, info) => {
        if (info.offset.x > SWIPE_DISMISS_THRESHOLD || info.velocity.x > 500) {
          dismiss('user');
        }
      }}
      initial={{ opacity: 0, y: -18, scale: 0.6, filter: 'blur(2px)' }}
      animate={{ opacity: 1, x: 0, y: 0, scale: 1, filter: 'blur(0px)' }}
      exit={{
        opacity: 0,
        x: 400,
        transition: { duration: 0.2, ease: 'easeIn' },
      }}
      style={{ transformOrigin: 'top center' }}
      className={classList(
        'relative',
        'cursor-grab active:cursor-grabbing',
        'bg-surface',
        'rounded-[16px]',
        'shadow-[0_8px_24px_rgba(0,0,0,0.2),0_2px_8px_rgba(0,0,0,0.15)]',
        'smoothing-default',
        'dark:border dark:border-border-strong',
        'mr-5',
        'max-w-[420px]'
      )}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 24,
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onAnimationComplete={handleAnimationComplete}
    >
      <HoverContext.Provider value={showDismiss}>
        <AnimatePresence mode="wait">
          {showDismiss && <DismissButton key="dismiss" />}
          {showDismiss && queueCount > 0 && <ClearAllButton key="clearAll" />}
        </AnimatePresence>
        {children}
      </HoverContext.Provider>
    </motion.div>
  );
};

const DismissButton = () => {
  const dismiss = lbNotificationsApis.useDismiss();

  return (
    <motion.button
      style={{ transformOrigin: 'center' }}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
      }}
      transition={{
        type: 'keyframes',
        ease: 'easeOut',
        duration: 0.05,
      }}
      className={classList(
        'absolute',
        'z-50',
        'size-6 min-w-6 max-w-6',
        'bg-surface',
        'dark:border dark:border-border-strong',
        'rounded-full',
        'focus:outline-none',
        'shadow-[0_3px_12px_rgba(0,0,0,0.15),0_1px_4px_rgba(0,0,0,0.1)]',
        'smoothing-default',
        'flex items-center justify-center',
        '-left-1.5 -top-1.5',
        'group'
      )}
      whileTap={{
        scale: 0.8,
        transition: {
          type: 'spring',
          stiffness: 300,
          damping: 24,
        },
      }}
      onClick={() => dismiss('user')}
    >
      <Icons.cancel
        strokeWidth={2}
        className="size-3.5 text-foreground-muted group-hover:text-foreground-subtle transition-colors"
      />
    </motion.button>
  );
};

const ClearAllButton = () => {
  const clearAll = lbNotificationsApis.useClearAll();

  return (
    <motion.button
      style={{ transformOrigin: 'center' }}
      initial={{ opacity: 0 }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
      }}
      transition={{
        type: 'keyframes',
        ease: 'easeOut',
        duration: 0.05,
      }}
      className={classList(
        'absolute',
        'z-50',
        'h-6 px-3',
        'bg-surface',
        'dark:border dark:border-border-strong',
        'rounded-full',
        'focus:outline-none',
        'shadow-[0_3px_12px_rgba(0,0,0,0.15),0_1px_4px_rgba(0,0,0,0.1)]',
        'smoothing-default',
        'flex items-center justify-center',
        'left-5 -top-1.5',
        'group'
      )}
      whileTap={{
        scale: 0.8,
        transition: {
          type: 'spring',
          stiffness: 300,
          damping: 24,
        },
      }}
      onClick={clearAll}
    >
      <span className="text-xs font-medium text-foreground-muted group-hover:text-foreground-subtle transition-colors">
        Clear All
      </span>
    </motion.button>
  );
};

const Content = ({
  children,
  className,
  ...props
}: ComponentPropsWithRef<'div'>) => (
  <div
    className={classList('overflow-hidden rounded-[16px]', className)}
    {...props}
  >
    {children}
  </div>
);

const Header = ({ children }: ViewProps) => (
  <div className="text-xs flex flex-col line-clamp-2">{children}</div>
);

const Title = ({ children }: ViewProps) => (
  <div className="text-foreground font-bold">{children}</div>
);

const Description = ({ children }: ViewProps) => (
  <div className="text-foreground-subtle line-clamp-6">{children}</div>
);

// eslint-disable-next-line react-refresh/only-export-components
export const LbNotifications = {
  Root,
  Frame,
  Content,
  Header,
  Title,
  Description,
};

const notificationContent: Record<string, ReactNode> = {
  'google-accessibility': (
    <span>
      {`Turn on screen reader support (${formatShortcutKeys(['mod', 'option', 'z'])}) so Littlebird can read your Google Docs.`}
    </span>
  ),
};

const BASIC_NOTIFICATION_DISMISS_MS = 5_000;

function resolveAutoDismissMs(
  value: boolean | number | undefined
): number | null {
  if (value === false) return null;
  if (typeof value === 'number') return value;
  return BASIC_NOTIFICATION_DISMISS_MS;
}

export function BasicNotification() {
  const notification = lbNotificationsApis.useNotification();
  const payload = lbNotificationsApis.useBasicNotificationPayload();
  const dismiss = lbNotificationsApis.useDismiss();
  const dismissMs = resolveAutoDismissMs(payload?.autoDismiss);
  useNotificationTimer({
    durationMs: dismissMs,
    onComplete: dismiss,
  });

  if (!payload) return null;

  const { title, message, contentKey, route, cta, actions } = payload;

  const handleClick = async () => {
    dismiss();

    if (!route) {
      electronApis.window.focusMainWindow();
      return;
    }

    trackEvent('PUSH_NOTIFICATION_CLICKED', {
      title,
      route: route.to,
    });

    lbNotificationsApis.navigateToMainWindow(route.to as AnyRoute['to'], {
      params: route.params,
      search: route.search,
      hash: route.hash,
    });
  };

  const handleAction = (actionId: string) => {
    if (notification?.id) {
      electronApis.lbNotifications.sendAction({
        notificationId: notification.id,
        actionId,
      });
    }
    dismiss();
  };

  const richContent = contentKey ? notificationContent[contentKey] : null;

  return (
    <LbNotifications.Content className="p-3 min-w-[300px] max-w-[350px]">
      <YStack className="gap-3">
        <XStack
          className="items-center gap-3 mr-4 cursor-pointer"
          onClick={handleClick}
        >
          <img
            src={appIconUrl}
            alt=""
            className="size-10 min-w-10 max-w-10 rounded-xl"
          />
          <LbNotifications.Header>
            <LbNotifications.Title>{title}</LbNotifications.Title>
            <LbNotifications.Description>
              {richContent ?? message}
            </LbNotifications.Description>
            {cta && (
              <span className="text-brand-cyan-cornflower-blue dark:text-brand-jordy-blue text-xs font-medium mt-1">
                {cta}
              </span>
            )}
          </LbNotifications.Header>
        </XStack>
        {actions && actions.length > 0 && (
          <XStack className="gap-2 justify-end">
            {actions.map((action) => (
              <button
                key={action.id}
                type="button"
                className={classList(
                  'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                  action.variant === 'destructive'
                    ? 'text-danger-11 hover:bg-danger-3'
                    : 'text-foreground bg-secondary hover:bg-secondary/80'
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAction(action.id);
                }}
              >
                {action.label}
              </button>
            ))}
          </XStack>
        )}
      </YStack>
    </LbNotifications.Content>
  );
}
